import { boolify } from './utils/common'
import { ensureTrailingSlash, removeTrailingSlash } from './utils/url'

/*
 * Backend API base URL — e.g.: `https://yv1ybf2bmh.execute-api.eu-west-1.amazonaws.com/nonprod`
 */
export const API_BASE_URL = removeTrailingSlash(process.env.VUE_APP_API_BASE_URL)

/**
 * Single Sign-On location to where users should be redirected to sign in.
 */
export const AUTH_SSO_URL = removeTrailingSlash(process.env.VUE_APP_AUTH_SSO_URL)

/**
 * Callback location to where users should be taken back after signing in. Must end in a forward slash
 * to be accepted by AWS Cognito `/oauth2/token` callback.
 */
export const AUTH_REDIRECT_URI = ensureTrailingSlash(process.env.VUE_APP_AUTH_REDIRECT_URI)

/**
 * Default authorization token used when `localStorage` is empty. Typically used for testing
 * purposes during development.
 */
export const AUTH_TOKEN = process.env.VUE_APP_AUTH_TOKEN?.trim()

/**
 * AWS Cognito URL.
 */
export const AWS_COGNITO_URL = removeTrailingSlash(process.env.VUE_APP_AWS_COGNITO_URL)

/**
 * AWS Cognito `clientId`.
 */
export const AWS_COGNITO_CLIENT_ID = process.env.VUE_APP_AWS_COGNITO_CLIENT_ID?.trim()

/**
 * Display a `FreezePeriodBanner` and prevent policy deployments during the specified time period.
 * Follows a `[YYYY-]MM-DD['T'HH:mm] "/" iso-8601-duration` format — e.g.: "2026-06-24/P1D" freezes
 * deployments from Jun 24th 00:00 to Jun 25th 00:00 (UTC).
 */
export const FREEZE_PERIOD = process.env.VUE_APP_FREEZE_PERIOD?.trim()

/**
 * Slack message for the code freeze period.
 */
export const FREEZE_PERIOD_SLACK_MESSAGE_URL = process.env.VUE_APP_FREEZE_PERIOD_SLACK_MESSAGE_URL?.trim()

/**
 * Bitbucket instance (i.e.: Stash) base URL.
 */
export const BITBUCKET_BASE_URL = removeTrailingSlash(process.env.VUE_APP_BITBUCKET_BASE_URL)

/**
 * Git provider for repository hosting (bitbucket or github).
 * Defaults to 'bitbucket' for backward compatibility.
 */
export const GIT_PROVIDER = (process.env.VUE_APP_GIT_PROVIDER?.trim() ?? 'bitbucket') as 'bitbucket' | 'github'

/**
 * GitHub Enterprise base URL (e.g., 'https://klarna.ghe.com').
 */
export const GITHUB_BASE_URL = removeTrailingSlash(process.env.VUE_APP_GITHUB_BASE_URL)

/**
 * GitHub organization name (e.g., 'klarna-iris' for production, 'klarna-iris-staging' for staging/performance).
 */
export const GITHUB_ORGANIZATION = process.env.VUE_APP_GITHUB_ORGANIZATION?.trim()

/**
 * Repository namespace for policies (e.g., 'IPRP').
 */
export const GIT_REPO_NAMESPACE_POLICY = process.env.VUE_APP_GIT_REPO_NAMESPACE_POLICY?.trim()

/**
 * Repository namespace for rules (e.g., 'IPRR').
 */
export const GIT_REPO_NAMESPACE_RULE = process.env.VUE_APP_GIT_REPO_NAMESPACE_RULE?.trim()

/**
 * Repository namespace for user functions (e.g., 'IPRF').
 */
export const GIT_REPO_NAMESPACE_USER_FUNCTION = process.env.VUE_APP_GIT_REPO_NAMESPACE_USER_FUNCTION?.trim()

/**
 * Klarna Wiki base URL.
 */
export const KLARNA_WIKI_BASE_URL = removeTrailingSlash(process.env.VUE_APP_KLARNA_WIKI_BASE_URL)

/**
 * Atlassian JIRA instance URL.
 */
export const JIRA_BASE_URL = removeTrailingSlash(process.env.VUE_APP_JIRA_BASE_URL)

/**
 * Wikibase base URL.
 *
 * This is where Actionable Improvements (AIM) are tracked.
 */
export const KLARNA_WIKIBASE_BASE_URL = removeTrailingSlash(process.env.VUE_APP_KLARNA_WIKIBASE_BASE_URL)

/**
 * `git` branch where releases are pushed to and tracked.
 */
export const GIT_RELEASE_BRANCH = process.env.VUE_APP_GIT_RELEASE_BRANCH?.trim() ?? 'release'

/**
 * A list of Klarna accountability team codes for Consumer Banking.
 */
export const CONSUMER_BANKING_TEAM_CODES = Object.freeze(
  process.env.VUE_APP_CONSUMER_BANKING_TEAM_CODES?.trim().split(/\s*,\s*/) ?? []
)

/**
 * Iris team Slack channel name — e.g.: #team-iris-decision-engine-dbs
 */
export const IRIS_TEAM_SLACK_CHANNEL_NAME = process.env.VUE_APP_IRIS_TEAM_SLACK_CHANNEL_NAME?.trim()

/**
 * Iris team Slack channel URL.
 */
export const IRIS_TEAM_SLACK_CHANNEL_URL = removeTrailingSlash(process.env.VUE_APP_IRIS_TEAM_SLACK_CHANNEL_URL)

/**
 * While in "maintenance mode", the application will only show a
 * static splash screen. All navigation routes are disabled.
 */
export const MAINTENANCE_MODE = boolify(process.env.VUE_APP_MAINTENANCE_MODE)

/**
 * Datadog base URL used to link to policy specific dashboards.
 */
export const DATADOG_BASE_URL = removeTrailingSlash(process.env.VUE_APP_DATADOG_BASE_URL)

/**
 * Background color of the top navbar used as a visual cue to distinguish between environments.
 */
export const MAIN_NAVBAR_COLOR = process.env.VUE_APP_MAIN_NAVBAR_COLOR?.trim()

/**
 * Request payloads larger than this (in bytes) will be compressed using `gzip` before being
 * sent to backend services. Any set value other than a positive number will be coerced to `undefined`.
 */
export const GZIP_COMPRESSION_BYTE_THRESHOLD =
  process.env.VUE_APP_GZIP_COMPRESSION_BYTE_THRESHOLD &&
  /^\d+\.?\d*$/.test(process.env.VUE_APP_GZIP_COMPRESSION_BYTE_THRESHOLD)
    ? Number(process.env.VUE_APP_GZIP_COMPRESSION_BYTE_THRESHOLD)
    : undefined

/**
 * Initial number of results requested from ElasticSearch search endpoints (can be changed by users).
 * Defaults to `20`.
 */
export const DEFAULT_SEARCH_SIZE = process.env.VUE_APP_DEFAULT_SEARCH_SIZE
  ? Number(process.env.VUE_APP_DEFAULT_SEARCH_SIZE)
  : 20

/**
 * A list of policy ids whose deployments are currently prevented
 * (i.e., the "Deploy policy" button would be disabled for all non-admin users).
 */
export const DISABLED_DEPLOYMENT_POLICY_IDS = Object.freeze(
  process.env.VUE_APP_DISABLED_DEPLOYMENT_POLICY_IDS?.trim()
    .toLowerCase()
    .split(/\s*,\s*/) ?? []
)

/**
 * Display a Holiday banner in the top navigation bar during the specified time period.
 */
export const HOLIDAY_SEASON_PERIOD = process.env.VUE_APP_HOLIDAY_SEASON_PERIOD?.trim()

/**
 * Display an alert banner in the top navigation bar during the specified time period.
 * If not provided, but `ALERT_BANNER_MESSAGE` is, the banner will be displayed indefinitely.
 */
export const ALERT_BANNER_PERIOD = process.env.VUE_APP_ALERT_BANNER_PERIOD?.trim()

/**
 * Message displayed in the alert banner.
 */
export const ALERT_BANNER_MESSAGE = process.env.VUE_APP_ALERT_BANNER_MESSAGE?.trim()

/**
 * Icon displayed in the alert banner (optional).
 */
export const ALERT_BANNER_ICON = process.env.VUE_APP_ALERT_BANNER_ICON?.trim()

/**
 * Whether to expose a Monaco instance globally at `window.document._monaco`.
 * This is useful for debugging purposes and Cypress E2E tests that need to
 * access the Monaco editor instance to change its state or content.
 */
export const MONACO_EDITOR_GLOBAL = boolify(process.env.VUE_APP_MONACO_EDITOR_GLOBAL)
